import { ErrorModalTypes, useBoundStore } from '@Store/index';
import { useMutation } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { Icons } from '@Components/icon/Icon';
import { t } from 'i18next';
import { ServerErrorResponse } from '@Types/index';
interface IProps {
  files: FormData[];
  handleReset: () => void;
  reportErrorDuringFileUpload: (errorCode: string, httpStatus: number, errorMessage: string) => void;
}
export const useFileUpload = ({ files, handleReset, reportErrorDuringFileUpload }: IProps) => {
  const { navigateToSpecificPage } = useNavigationManager();
  const setErrorModal = useBoundStore.getState().errorSlice.setErrorModal;

  return useMutation({
    mutationFn: () => uploadFilesAndSubmitTokens(files),
    mutationKey: ['getFileTokens'],
    onSuccess: () => navigateToSpecificPage(RoutesMap.UNDER_REVIEW),
    onError: (error: ServerErrorResponse) => {
      const localizedErrorMessage = error?.response?.data?.errors?.[0]?.localizedMessage;
      const error0 = error?.response?.data?.errors[0];
      reportErrorDuringFileUpload(String(error0.codeName), error0.code, error0.message);
      
      setErrorModal({
        error: 'An unknown error occurred',
        modalProps: {
          title: t('enErrors:default.title'),
          content: localizedErrorMessage ?? 'enErrors:default.description',
          icon: Icons.Oops,
          buttonText: t('enErrors:close'),
          type: ErrorModalTypes.GENERAL,
        },
      });
      handleReset();
    },
  });
};

export const uploadFilesAndSubmitTokens = async (files: FormData[]) => {
  const { customerId } = useBoundStore.getState().customer;
  const {
    getKybData,
    onboardingSlice: { getIsEmbedded },
  } = useBoundStore.getState();
  const tokenPromises = files.map(async (file) => {
    const url = getIsEmbedded()
      ? `v3/secureonboarding/partner/user/${customerId}/llc/upload`
      : `v3/secureonboarding/user/${customerId}/llc/upload`;
    const { data } = await HttpService.post(url, file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  });
  const kybData = getKybData();
  const tokens = await Promise.all(tokenPromises);
  const tokensToSend = tokens.map((token) => token.fileId);
  const tokenUploadUrl = getIsEmbedded()
    ? `v3/secureonboarding/partner/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`
    : `v3/secureonboarding/user/${customerId}/llc/${kybData.business.externalId}/submitDocs`;
  await HttpService.post(tokenUploadUrl, {
    documentTokens: tokensToSend,
  });
};
